import React, { useEffect, useMemo, useState } from "react";
import { Star, ArrowsClockwise, CaretUp, CaretDown } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const STAR_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#10b981"]; // 1★→5★

function Bar({ value, total, color, label }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-6 inline-flex items-center gap-0.5 text-zinc-400 font-mono justify-end">
        {label}<Star size={9} weight="fill" style={{ color }} />
      </span>
      <div className="flex-1 h-2 rounded-full bg-ink-800 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-10 text-right text-zinc-500 font-mono tabular-nums">{value}</span>
    </div>
  );
}

function SortHead({ label, k, sort, setSort }) {
  const active = sort.key === k;
  return (
    <button
      onClick={() => setSort({ key: k, dir: active && sort.dir === "desc" ? "asc" : "desc" })}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] ${active ? "text-neon-cyan" : "text-zinc-500 hover:text-zinc-300"}`}
      data-testid={`ratings-sort-${k}`}
    >
      {label}
      {active && (sort.dir === "desc" ? <CaretDown size={10} weight="bold" /> : <CaretUp size={10} weight="bold" />)}
    </button>
  );
}

export default function RatingDistribution() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "rank", dir: "asc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics/ratings");
      setRows(data?.per_slot || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const mul = sort.dir === "asc" ? 1 : -1;
      const av = a[sort.key], bv = b[sort.key];
      if (av === bv) return a.rank - b.rank;
      return av > bv ? mul : -mul;
    });
    return out;
  }, [rows, sort]);

  const grand = useMemo(() => {
    const acc = [0, 0, 0, 0, 0];
    let total = 0, sum = 0;
    rows.forEach((r) => {
      for (let i = 0; i < 5; i++) acc[i] += r.histogram[i];
      total += r.total;
      sum += r.avg * r.total;
    });
    const avg = total > 0 ? (sum / total) : 0;
    return { acc, total, avg };
  }, [rows]);

  return (
    <section data-testid="rating-distribution">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2">Rating Distribution</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">How fans are voting.</h2>
          <p className="text-sm text-zinc-500 mt-2">1★ → 5★ breakdown per slot. Tap a column header to sort.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="ratings-reload"><ArrowsClockwise size={14} /> Reload</button>
      </div>

      {/* Grand totals */}
      <div className="rounded-xl border border-ink-600 bg-ink-900/40 p-5 mb-6" data-testid="ratings-grand">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
          <div className="text-[10px] font-bold tracking-[0.25em] text-zinc-400 uppercase">All Slots Combined</div>
          <div className="font-mono text-sm text-zinc-300">
            <span className="text-white font-semibold">{grand.total}</span> votes
            <span className="text-zinc-600"> · </span>
            <span className="text-neon-cyan">avg {grand.avg.toFixed(2)}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          {[5, 4, 3, 2, 1].map((star, idx) => (
            <Bar
              key={star}
              label={star}
              value={grand.acc[star - 1]}
              total={grand.total}
              color={STAR_COLORS[star - 1]}
            />
          ))}
        </div>
      </div>

      {/* Per-slot table */}
      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-ink-600 bg-ink-800/30">
          <div className="col-span-1"><SortHead label="Rank" k="rank" sort={sort} setSort={setSort} /></div>
          <div className="col-span-6 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Distribution</div>
          <div className="col-span-2 text-right"><SortHead label="Avg" k="avg" sort={sort} setSort={setSort} /></div>
          <div className="col-span-3 text-right"><SortHead label="Total" k="total" sort={sort} setSort={setSort} /></div>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : (
          <ul>
            {sorted.map((row) => (
              <li key={row.rank} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-ink-700/60 last:border-0 items-center" data-testid={`ratings-row-${row.rank}`}>
                <div className="col-span-1 font-mono text-xs text-neon-cyan">#{String(row.rank).padStart(2, "0")}</div>
                <div className="col-span-6 space-y-1">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <Bar
                      key={star}
                      label={star}
                      value={row.histogram[star - 1]}
                      total={row.total || 1}
                      color={STAR_COLORS[star - 1]}
                    />
                  ))}
                </div>
                <div className="col-span-2 text-right">
                  <span className="font-mono text-sm text-white" data-testid={`ratings-avg-${row.rank}`}>{row.avg.toFixed(2)}</span>
                  <Star size={11} weight="fill" className="inline ml-1 text-amber-300" />
                </div>
                <div className="col-span-3 text-right">
                  <span className="font-mono text-sm text-zinc-300" data-testid={`ratings-total-${row.rank}`}>{row.total}</span>
                  <div className="text-[10px] text-zinc-500 font-mono">{row.display_count.toLocaleString()} pts</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
